import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col, Typography, Input, Select } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../components/CustomButton/Button";
import SupplierTable from "./Components/SupplierTable";
import NewSupplierModal from "./Components/NewSupplierModal";
import { getSuppliers } from "../../redux/slices/supplierSlice";
import { count } from "../../redux/slices/AuthSlice";
import "./Supplier.scss";

const { Title } = Typography;

const STATUS_OPTIONS = [
  { value: "ALL", label: "All Status" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const Supplier = () => {
  const dispatch = useDispatch();
  const { suppliersList = [], loading = false } = useSelector((state) => state.supplier);
  const { activeCategory } = useSelector((state) => state.dealCategory || {});

  const [selectedStatus, setSelectedStatus] = useState("ACTIVE");
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch suppliers whenever status changes
  const fetchSuppliers = useCallback(() => {
    dispatch(getSuppliers({ status: selectedStatus }));
    if (activeCategory?.dealCategoryId) {
      dispatch(count({ type: "ALL", dealCategoryId: activeCategory.dealCategoryId }));
    }
  }, [dispatch, selectedStatus, activeCategory]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  // Client-side search filtering for download and total count calculation
  const filteredData = useMemo(() => {
    if (!searchText?.trim()) return suppliersList;
    const query = searchText.toLowerCase().trim();
    return (suppliersList || []).filter(
      (item) =>
        item.supplierName?.toLowerCase().includes(query) ||
        item.pocName?.toLowerCase().includes(query) ||
        item.pocEmail?.toLowerCase().includes(query) ||
        item.pocMobile?.toLowerCase().includes(query) ||
        item.bankName?.toLowerCase().includes(query) ||
        item.supplierId?.toLowerCase().includes(query) ||
        item.status?.toLowerCase().includes(query)
    );
  }, [suppliersList, searchText]);

  const totalSuppliersCount = filteredData?.length || 0;

  return (
    <div className="supplier-container">
      <Row
        align="middle"
        justify="space-between"
        className="supplier-header"
        gutter={[16, 16]}
      >
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Suppliers ({totalSuppliersCount})
          </Title>
        </Col>
        <Col>
          <Row gutter={12} align="middle">
            <Col>
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                style={{ width: 140, height: 40 }}
                options={STATUS_OPTIONS}
              />
            </Col>
            <Col>
              <Input
                placeholder="Search Supplier"
                prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260, height: 40 }}
                allowClear
              />
            </Col>
            <Col>
              <CustomButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setModalOpen(true)}
                style={{ height: 40 }}
              >
                Add New Supplier
              </CustomButton>
            </Col>
          </Row>
        </Col>
      </Row>

      <SupplierTable
        data={suppliersList}
        loading={loading}
        searchText={searchText}
        selectedStatus={selectedStatus}
        onRefresh={fetchSuppliers}
      />

      {modalOpen && (
        <NewSupplierModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={fetchSuppliers}
          existingSuppliers={suppliersList}
        />
      )}
    </div>
  );
};

export default Supplier;

